<!-- 商品管理 V2 - 商品列表表格 -->
<template>
  <div class="table product-table">
    <!-- 列表加载中遮罩层 -->
    <div v-if="loading" class="loading-mask">
      <i class="el-icon-loading"></i>
      <span>{{ $t('message.productV2Loading') }}</span>
    </div>

    <!-- 表头：固定列标题，与下方数据行 grid 列宽一一对应 -->
    <ol class="table-grid table-head">
      <!-- 全选复选框 -->
      <li class="col-select">
        <input type="checkbox" :checked="allSelected" @change="toggleSelection($event.target.checked)">
      </li>
      <!-- 序号 -->
      <li class="col-no">NO</li>
      <!-- 客户 ID / 客户名称 -->
      <li class="col-user">{{ $t('message.userInfo') }}</li>
      <!-- 商品管理番号 -->
      <li class="col-code">{{ $t('message.managementDesignation') }}</li>
      <!-- 商品主图、中文名、SKU 规格 -->
      <li class="col-product">{{ $t('message.productV2ProductInfo') }}</li>
      <!-- 下单方案单选列表（切换后联动后续列数据） -->
      <li class="col-plan plan-divider">{{ $t('message.productV2PlanSelection') }}</li>
      <!-- 当前方案对应的采购链接 -->
      <li class="col-link">{{ $t('message.productV2PurchaseLink') }}</li>
      <!-- 当前方案的附加服务 OPTION -->
      <li class="col-option">{{ $t('message.additionalServices') }}</li>
      <!-- 当前方案绑定的标签信息 -->
      <li class="col-tag">{{ $t('message.tagInformation') }}</li>
      <!-- 当前方案绑定的辅料及消耗量 -->
      <li class="col-material">{{ $t('message.bindingAuxiliaryMaterials') }}</li>
      <!-- 当前方案的商品管理备注 -->
      <li class="col-remark">{{ $t('message.productManagementRemarks') }}</li>
      <!-- 当前方案的业务备注 -->
      <li class="col-business">{{ $t('message.productV2BusinessRemark') }}</li>
      <!-- 当前方案的通关品名 / 材质 / HSCODE -->
      <li class="col-clearance">{{ $t('message.customsClearanceInformation') }}</li>
      <!-- 维护操作、最后更新时间 -->
      <li class="col-operation">{{ $t('message.operation') }}</li>
    </ol>

    <!-- 商品数据行：每条记录对应一个 detail 容器 -->
    <div v-for="(item, index) in tableData" :key="item.sys_code" class="detail">
      <!-- 行顶商品标签条（client_product_tag，有数据时才显示） -->
      <div v-if="item.client_product_tag && item.client_product_tag.length" class="row-flags">
        <span
          v-for="tag in item.client_product_tag"
          :key="tag.id"
          :style="{ borderColor: tag.color, color: tag.color }"
        >{{ tag.name }}</span>
      </div>

      <!-- 主表格行：方案超过 2 个时增加行高（has-many-plans） -->
      <ul class="table-grid table-row" :class="{ 'has-many-plans': getPlanList(item).length > 2 }">
        <!-- 选择列：复选框 + 左下角维护状态角标，与顶部状态 Tab 保持一致 -->
        <li class="col-select">
          <input v-model="item.checked" type="checkbox" @change="selectionChange">
          <span
            v-if="getMaintenanceStatus(item)"
            class="status"
            :class="getMaintenanceStatus(item).className"
          >{{ getMaintenanceStatus(item).label }}</span>
        </li>

        <!-- 序号列：跨页连续编号 + 展开/收起按钮 -->
        <li class="col-no">
          <span>{{ indexMethod(index) }}</span>
          <button type="button" class="expand-button" @click="item.show = !item.show">
            {{ item.show ? $t('message.collapse') : $t('message.expand') }}
            <i :class="item.show ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </button>
        </li>

        <!-- 客户信息列 -->
        <li class="col-user">
          <div>
            <p>ID:{{ item.user && item.user.id }}</p>
            <p>{{ item.user && item.user.realname }}</p>
          </div>
        </li>

        <!-- 管理番号列：SPU / 立即绑定（按钮样式、宽度随内容）、番号文本、采购链接 -->
        <li class="col-code">
          <div class="template code-cell">
            <!-- SPU：有值展示 spu.spu；无值展示立即绑定；样式与按钮一致，宽度随内容撑开 -->
            <div
              v-if="getBoundSpu(item)"
              class="spu-value"
              @click="$emit('bind-spu', item)"
            >
              <span>{{ getBoundSpu(item).spu }}</span>
              <i class="el-icon-edit-outline"></i>
            </div>
            <button
              v-else
              type="button"
              class="spu-bind-btn"
              @click.stop="$emit('bind-spu', item)"
            >{{ $t('message.productV2BindNow') }}</button>
            <!-- 管理番号，点击编辑图标触发 edit-code 事件 -->
            <p>
              {{ item.client_sku_code }}
              <i class="el-icon-edit-outline" @click="$emit('edit-code', item)"></i>
            </p>
            <!-- 商品级采购链接历史 -->
            <button
              type="button"
              class="code-link"
              @click.stop="$emit('product-link', item)"
            ><i class="el-icon-link"></i></button>
          </div>
        </li>

        <!-- 商品信息列：主图缩略图 + 中文标题 + SKU 规格明细 -->
        <li class="col-product">
          <div class="product-info">
            <el-image
              v-if="item.pic"
              class="product-thumb"
              :src="item.pic"
              fit="cover"
              :preview-src-list="[item.pic]"
            />
            <div class="sku-info">
              <p v-for="(sku, skuIndex) in item.sku" :key="skuIndex">
                {{ sku.key }}：{{ sku.value }}
              </p>
            </div>
          </div>
        </li>

        <!-- 方案选择列：radio 切换当前激活方案，影响后续列展示数据 -->
        <li class="col-plan plan-divider">
          <div class="plan-list">
            <div
              v-for="(plan, planIndex) in getPlanList(item)"
              :key="plan.id || planIndex"
              class="plan-item"
              :class="{ selected: getActivePlanIndex(item) === planIndex }"
              @click="viewPlan(item, planIndex)"
            >
              <input
                type="radio"
                :name="'plan-' + item.sys_code"
                :checked="getDefaultPlanIndex(item) === planIndex"
                @click.stop
                @change="selectPlan(item, planIndex)"
              >
              <span @click.stop="viewPlan(item, planIndex)">{{ plan.name }}</span>
            </div>
          </div>
        </li>

        <!-- 采购链接列：展示当前激活方案的第一条链接 -->
        <li class="col-link">
          <el-popover v-if="getActivePlan(item).link" placement="top" width="360" trigger="click">
            <div class="full-link-popover">
              <a class="full-link-text" :href="getActivePlan(item).link" target="_blank" rel="noopener noreferrer">{{ getActivePlan(item).link }}</a>
            </div>
            <button slot="reference" type="button" class="view-link-button">查看</button>
          </el-popover>
          <span v-else>-</span>
          <el-popover
            v-if="getActivePlan(item).children && getActivePlan(item).children.length"
            placement="top"
            width="320"
            trigger="click"
          >
            <div class="combo-link-popover">
              <p v-for="(child, childIndex) in getActivePlan(item).children" :key="child.id || childIndex">
                组合商品{{ childIndex + 1 }}：
                <a v-if="child.link" :href="child.link" target="_blank" rel="noopener noreferrer">{{ child.link }}</a>
                <span v-else>{{ child.goods_title }}</span>
              </p>
            </div>
            <button slot="reference" type="button" class="view-link-button combo-view-button">组套商品</button>
          </el-popover>
        </li>

        <!-- 附加服务列：当前方案的 OPTION 列表 -->
        <li class="col-option">
          <el-tooltip
            v-if="getActivePlanList(item, 'option').length"
            placement="right"
            effect="light"
            trigger="hover"
            popper-class="product-table-tooltip"
          >
            <!-- 完整附加服务内容放在 Element UI Tooltip 中，避免撑开列表行高。 -->
            <div slot="content" class="option-tooltip-content">
              <p v-for="(option, optionIndex) in getActivePlanList(item, 'option')" :key="option.id || optionIndex">
                {{ optionIndex + 1 }}. {{ option.name }}&nbsp;&nbsp;{{ option.price }}{{ option.price_type === 1 ? '%' : '元/点' }}
              </p>
            </div>
            <!-- 单元格内保留原有预览，鼠标移入后由 Tooltip 展示完整内容。 -->
            <div class="template option-preview">
              <p v-for="(option, optionIndex) in getActivePlanList(item, 'option')" :key="option.id || optionIndex">
                {{ optionIndex + 1 }}. {{ option.name }}&nbsp;&nbsp;{{ option.price }}{{ option.price_type === 1 ? '%' : '元/点' }}
              </p>
            </div>
          </el-tooltip>
          <span v-else>-</span>
        </li>

        <!-- 标签信息列：仓库类别、标签条码、商品编码及文本信息 -->
        <li class="col-tag">
          <el-tooltip
            v-if="getActivePlanList(item, 'order_detail_tag').length"
            placement="right"
            effect="light"
            trigger="hover"
            popper-class="product-table-tooltip"
          >
            <!-- 完整标签字段通过 Tooltip 展示，内容超长时不影响表格布局。 -->
            <div slot="content" class="tag-tooltip-content">
              <div v-for="(tag, tagIndex) in getActivePlanList(item, 'order_detail_tag')" :key="tag.id || tagIndex" class="tag-row">
                <p><span>仓库类别</span><em>{{ tag.type }}</em></p>
                <p><span>标签条码</span><em>{{ tag.no }}</em></p>
                <p><span>商品编码</span><em>{{ tag.goods_no }}</em></p>
                <p><span>文本信息1</span><em>{{ tag.text_line_one }}</em></p>
                <p><span>文本信息2</span><em>{{ tag.text_line_two }}</em></p>
                <p><span>文本信息3</span><em>{{ tag.text_line_three }}</em></p>
                <p><span>文本信息4</span><em>{{ tag.text_line_four }}</em></p>
              </div>
            </div>
            <!-- 单元格内保留紧凑预览，鼠标移入后由 Tooltip 展示完整字段。 -->
            <div class="template tag-preview">
              <div v-for="(tag, tagIndex) in getActivePlanList(item, 'order_detail_tag')" :key="tag.id || tagIndex" class="tag-row">
                <p><span>仓库类别</span><em>{{ tag.type }}</em></p>
                <p><span>标签条码</span><em>{{ tag.no }}</em></p>
                <p><span>商品编码</span><em>{{ tag.goods_no }}</em></p>
                <p><span>文本信息1</span><em>{{ tag.text_line_one }}</em></p>
                <p><span>文本信息2</span><em>{{ tag.text_line_two }}</em></p>
                <p><span>文本信息3</span><em>{{ tag.text_line_three }}</em></p>
                <p><span>文本信息4</span><em>{{ tag.text_line_four }}</em></p>
              </div>
            </div>
          </el-tooltip>
          <span v-else>-</span>
        </li>

        <!-- 绑定辅料列：辅料名称 + 消耗点数 -->
        <li class="col-material">
          <div class="template">
            <div v-for="(material, materialIndex) in getActivePlanList(item, 'material')" :key="material.material_id || materialIndex" class="material-row">
              <p>{{ material.material_detail.name }}*{{ material.consume }}</p>
            </div>
            <span v-if="!getActivePlanList(item, 'material').length">-</span>
          </div>
        </li>

        <!-- 商品管理备注列 -->
        <li class="col-remark">
          <div
            class="template word-break"
            :title="getPlanNoticeText(item, 'remark')"
          >{{ getPlanNoticeText(item, 'remark') }}</div>
        </li>

        <!-- 业务备注列 -->
        <li class="col-business">
          <div
            class="template word-break"
            :title="getPlanNoticeText(item, 'y_remark')"
          >{{ getPlanNoticeText(item, 'y_remark') }}</div>
        </li>

        <!-- 通关信息列：中文品名 / 中文材质 / HSCODE / 税率 -->
        <li class="col-clearance">
          <div v-if="item.client_product_clearance" class="template">
            <p>品名：{{ item.client_product_clearance.pingming_zh }}</p>
            <p>材质：{{ item.client_product_clearance.material_zh }}</p>
            <p>HSCODE: {{ item.client_product_clearance.hscode }}</p>
            <p>税率: {{ item.client_product_clearance.tax_rate }}</p>
          </div>
          <span v-else>-</span>
        </li>

        <!-- 操作列：维护按钮 + 当前方案更新时间 -->
        <li class="col-operation">
          <div class="operation-cell">
            <button type="button" @click="$emit('maintain', item)">{{ $t('message.productV2Maintain') }}</button>
            <!-- 无需维护只在操作栏展示，避免与维护状态角标混在一起。 -->
            <span v-if="isNoNeedMaintenance(item)" class="operation-status no-need">{{ $t('message.productV2NoNeedStatus') }}</span>
            <p class="updated-at">
              {{ item.updated_at }}
            </p>
          </div>
        </li>
      </ul>

      <!-- 展开区域：点击「展开」后显示，展示商品级补充信息（非方案级） -->
      <div v-show="item.show" class="expand-content">
        <!-- 特殊商品：违禁类型 + 重量 + 尺寸 -->
        <section
          class="product-name"
          @mouseenter="hoverForbidCode = item.sys_code"
          @mouseleave="hoverForbidCode = ''"
        >
          <h4>{{ $t('message.specialProducts') }}</h4>
          <div class="forbid-wrap">
            <div class="forbid-list">
              <span v-for="(forbid, forbidIndex) in item.forbid_type_names" :key="forbidIndex">
                {{ forbid }}
              </span>
            </div>
            <div v-show="hoverForbidCode === item.sys_code" class="forbid-all">
              <span v-for="(forbid, forbidIndex) in item.forbid_type_names" :key="forbidIndex">
                {{ forbid }}
              </span>
            </div>
          </div>
          <p>{{ item.weight || 0 }}g</p>
          <p>{{ item.length || 0 }}cm*{{ item.width || 0 }}cm*{{ item.height || 0 }}cm</p>
        </section>

        <!-- 实物图：最多展示已有图片，无图时显示占位块 -->
        <section>
          <div class="section-head">
            <h4>{{ $t('message.physicalImage') }}</h4>
            <button
              v-if="item.client_product_image_real.length > 2"
              type="button"
              @click="$refs.imagePreviewDialog.open($t('message.physicalImage'), item.client_product_image_real)"
            >{{ $t('message.productV2ViewAll') }}></button>
          </div>
          <div class="image-list">
            <el-image
              v-for="(image, imageIndex) in item.client_product_image_real.slice(0, 2)"
              :key="image.id || imageIndex"
              :src="image.url"
              fit="cover"
              :preview-src-list="item.client_product_image_real.map(image => image.url)"
            />
          </div>
        </section>

        <!-- 包装图：最多展示已有图片，无图时显示占位块 -->
        <section>
          <div class="section-head">
            <h4>{{ $t('message.packingDiagram') }}</h4>
            <button
              v-if="item.client_product_image_pack.length > 2"
              type="button"
              @click="$refs.imagePreviewDialog.open($t('message.packingDiagram'), item.client_product_image_pack)"
            >{{ $t('message.productV2ViewAll') }}></button>
          </div>
          <div class="image-list">
            <el-image
              v-for="(image, imageIndex) in item.client_product_image_pack.slice(0, 2)"
              :key="image.id || imageIndex"
              :src="image.url"
              fit="cover"
              :preview-src-list="item.client_product_image_pack.map(image => image.url)"
            />
          </div>
        </section>

        <!-- 商品文件：按分类展示附件列表 -->
        <section class="file-list">
          <h4>{{ $t('message.productFile') }}</h4>
          <div class="file-categories">
            <div v-for="(category, categoryIndex) in item.client_product_file_cate" :key="categoryIndex">
              <strong>{{ category.cate_name }}</strong>
              <div class="file-category-items">
                <a
                  v-for="(file, fileIndex) in category.client_product_file"
                  :key="fileIndex"
                  :href="file.url"
                  target="_blank"
                  rel="noopener noreferrer"
                >{{ fileIndex + 1 }}.{{ file.name }}</a>
              </div>
            </div>
          </div>
        </section>

        <!-- 当前方案的采购 / 配货 / 检品要求 -->
        <section>
          <h4>{{ $t('message.productV2PurchaseRequirement') }}</h4>
          <p class="detail-notice">{{ getPlanNoticeText(item, 'purchase_notice') }}</p>
        </section>
        <section>
          <h4>{{ $t('message.productV2AllocationRequirement') }}</h4>
          <p class="detail-notice">{{ getPlanNoticeText(item, 'delivery_notice') }}</p>
        </section>
        <section>
          <h4>{{ $t('message.productV2InspectionRequirement') }}</h4>
          <p class="detail-notice">{{ getPlanNoticeText(item, 'inspect_notice') }}</p>
        </section>
      </div>
    </div>

    <!-- 无数据时的空状态 -->
    <div v-if="!loading && !tableData.length" class="empty">{{ $t('message.productV2NoData') }}</div>
    <image-preview-dialog ref="imagePreviewDialog" />
  </div>
</template>

<script>
import imagePreviewDialog from './imagePreviewDialog'

export default {
  components: { imagePreviewDialog },
  data() {
    return {
      hoverForbidCode: ''
    }
  },
  props: {
    tableData: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 10 }
  },
  computed: {
    // 是否全选
    allSelected() {
      return this.tableData.length > 0 && this.tableData.every(item => item.checked)
    }
  },
  methods: {
    // 跨页连续序号
    indexMethod(index) {
      return (this.page - 1) * this.pageSize + index + 1
    },
    // 列表行已绑定 SPU：{ id, spu, name, image }
    getBoundSpu(item) {
      const spu = item && item.spu
      if (!spu || typeof spu !== 'object') return null
      if (!spu.spu && !spu.id) return null
      return spu
    },
    // 获取下单方案列表
    getPlanList(item) {
      return Array.isArray(item.order_plans) ? item.order_plans.filter(Boolean) : []
    },
    getActivePlanIndex(item) {
      const index = Number(item.active_plan_index)
      return Number.isInteger(index) && index >= 0 ? index : 0
    },
    getDefaultPlanIndex(item) {
      const index = Number(item.default_plan_index)
      return Number.isInteger(index) && index >= 0 ? index : this.getActivePlanIndex(item)
    },
    getActivePlan(item) {
      return this.getPlanList(item)[this.getActivePlanIndex(item)] || {}
    },
    getActivePlanList(item, field) {
      const list = this.getActivePlan(item)[field]
      return Array.isArray(list) ? list.filter(Boolean) : []
    },
    isNoNeedMaintenance(item) {
      return Number(item.audit_type) === 2
    },
    getMaintenanceStatus(item) {
      const status = Number(item.maintain_status)
      const statusMap = {
        1: { label: this.$t('message.productV2PendingMaintenance'), className: 'pending' },
        2: { label: this.$t('message.productV2Maintaining'), className: 'maintaining' },
        3: { label: this.$t('message.productV2PendingAudit'), className: 'pending-audit' },
        4: { label: this.$t('message.productV2MaintenanceCompleted'), className: 'completed' }
      }
      return statusMap[status] || null
    },
    getPlanNoticeText(item, key) {
      const plan = this.getActivePlan(item)
      const notice = plan[key] || (plan.notice && plan.notice[key])
      return notice && notice.text ? notice.text : ''
    },
    // 操作列时间：第一行年月日，第二行时分秒
    splitDateTime(value) {
      const text = String(value || '').trim()
      if (!text) return { date: '', time: '' }
      const parts = text.split(/\s+/)
      if (parts.length >= 2) {
        return { date: parts[0], time: parts.slice(1).join(' ') }
      }
      return { date: text, time: '' }
    },
    viewPlan(item, planIndex) {
      // 点击方案内容只切换当前查看内容，不修改服务端默认方案。
      this.$set(item, 'active_plan_index', planIndex)
    },
    selectPlan(item, planIndex) {
      const previousViewIndex = this.getActivePlanIndex(item)
      const previousDefaultIndex = this.getDefaultPlanIndex(item)
      const plan = this.getPlanList(item)[planIndex]
      this.$set(item, 'active_plan_index', planIndex)
      // 列表中的选中方案就是默认方案，切换时同步更新服务端默认方案。
      if (!plan || !plan.id || previousDefaultIndex === planIndex) return
      this.$api.productManagementApi.orderPlanSetDefault({
        user_id: item.user_id,
        sys_code: item.sys_code,
        id: plan.id,
        is_default: 1
      }).then(res => {
        this.$set(item, 'default_plan_index', planIndex)
        this.getPlanList(item).forEach((currentPlan, index) => {
          this.$set(currentPlan, 'is_default', index === planIndex ? 1 : 0)
        })
        this.$message.success(res.msg || this.$t('message.saveSuccess'))
      }).catch(() => {
        // 接口失败时恢复原方案，避免页面选中状态与服务端不一致。
        this.$set(item, 'active_plan_index', previousViewIndex)
      })
    },
    // 勾选变化时通知父组件
    selectionChange() {
      this.$emit('selection-change', this.tableData.filter(item => item.checked))
    },
    // 表头全选/取消全选
    toggleSelection(checked) {
      this.tableData.forEach(item => {
        item.checked = checked
      })
      this.selectionChange()
      this.$forceUpdate()
    },
    // 一键展开/折叠所有行（供父组件调用）
    toggleAllRows(expanded) {
      this.tableData.forEach(item => {
        item.show = expanded
      })
      this.$forceUpdate()
    }
  }
}
</script>

<style lang="scss" scoped>
.product-table {
  position: relative;
  min-height: 120px;
  margin-top: 20px;
  width: 100%;
  color: #333;
  font-size: 14px;

  ol,
  ul { margin: 0; padding: 0; list-style: none; }
  p { margin: 3px 0; line-height: 18px; }
  a { color: #0d4fe6; }
}

.table-grid {
  display: grid;
  grid-template-columns: 48fr 80fr 90fr 170fr 190fr 190fr 110fr 202fr 138fr 150fr 140fr 140fr 150fr 150fr;
  width: 100%;
  min-width: 1420px;

  > li {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    min-width: 0;
    padding: 10px;
  }
}

.table-head {
  position: sticky;
  top: 0;
  z-index: 4;
  height: 54px;
  border-radius: 4px;
  background: #edeff4;

  > li { justify-content: center; text-align: center; }
}

.table-row {
  min-height: 120px;
  height: auto;
  background: #fff;

  > li:first-child { position: relative; }
}

.table-row.has-many-plans { min-height: 198px; }

.plan-divider {
  border-left: 1px solid #dcdfe6;
}

.col-select,
.col-no,
.col-link,
.col-operation { justify-content: center; }

.col-link { flex-direction: column; gap: 8px; }
.option-preview,
.tag-preview { width: 100%; min-width: 0; }
.option-tooltip-content { max-width: 360px; }
.option-tooltip-content p { margin: 0 0 6px; line-height: 20px; white-space: nowrap; }
.option-tooltip-content p:last-child { margin-bottom: 0; }
.tag-tooltip-content { max-width: 360px; }
.tag-tooltip-content .tag-row p em {
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
  overflow-wrap: anywhere;
}

.col-no {
  flex-direction: column;
  gap: 16px;
}

.detail {
  position: relative;
  width: 100%;
 
  
  border-bottom: 1px solid #dcdfe6;
  background: #fff;
}

.row-flags {
  position: absolute;
  z-index: 2;
  top: 2px;
  left: 2px;
  display: flex;
  gap: 6px;
}

.row-flags span {
  padding: 4px 6px;
  border: 1px solid;
  border-radius: 4px;
  background: #fff;
  font-size: 12px;
  line-height: 12px;
}

input[type="checkbox"] {
  appearance: auto;
  -webkit-appearance: checkbox;
  width: 28px;
  height: 28px;
  margin: 0;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  accent-color: #b4272d;
  cursor: pointer;
}

input[type="checkbox"]:checked {
  border-color: #b4272d;
  background: #fff;
  box-shadow: none;
}

input[type="radio"] {
  appearance: none;
  width: 20px;
  height: 20px;
  margin: 0;
  border: 1px solid #dcdfe6;
  border-radius: 50%;
  background: #fff;
}

input[type="radio"]:checked {
  border-color: #2386ff;
  box-shadow: inset 0 0 0 4px #fff;
  background: #2386ff;
}

.status {
  position: absolute;
  bottom: 0;
  left: 0;
  display: inline-block;
  width: fit-content;
  max-width: 100%;
  padding: 6px 10px;
  border-radius: 0 4px 0 0;
  color: #fff;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}

.status.pending { background: #b4272d; }
.status.maintaining { background: #e6a23c; }
.status.pending-audit { background: #409eff; }
.status.completed { background: #15ad3c; }

.template {
  width: 100%;
  max-height: 96px;
  overflow: hidden;
}
.code-cell.template {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: none;
  overflow: visible;
}
.word-break {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.plan-list { display: flex; flex-direction: column; gap: 6px; width: 100%; min-width: 0; }
.plan-item {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  height: auto;
  padding: 8px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
}
.plan-item span { min-width: 0; line-height: 18px; overflow-wrap: anywhere; }
.plan-item input[type="radio"] {
  flex: 0 0 20px;
  width: 20px;
  min-width: 20px;
  height: 20px;
  min-height: 20px;
  box-sizing: border-box;
}
.plan-item.selected { border-color: #2386ff; background: #f3f8ff; }

.view-link-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #315ede;
  white-space: nowrap;
  cursor: pointer;
}

.full-link-popover { line-height: 20px; }
.full-link-text { display: block; margin-bottom: 8px; color: #315ede; word-break: break-all; }
.full-link-popover a,
.combo-link-popover a { color: #315ede; }
.combo-view-button { display: block; margin-top: 8px; }
.combo-link-popover p { margin: 0 0 8px; color: #333; line-height: 20px; word-break: break-all; }

.code-cell > p {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f8f8f8;
  box-sizing: border-box;
}
.code-cell > p i { margin-left: 5px; color: #333; cursor: pointer; }
/* SPU / 立即绑定：按钮同款样式，宽度随内容自由撑开，居中显示 */
.spu-value,
.spu-bind-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  max-width: 100%;
  height: 32px;
  margin: 0 auto 10px;
  padding: 0 12px;
  border: 0;
  border-radius: 4px;
  background: #f2f6ff;
  color: #0d4fe6;
  font-size: 13px;
  line-height: 32px;
  white-space: nowrap;
  cursor: pointer;
  box-sizing: border-box;
}
.spu-value span {
  overflow: hidden;
  text-overflow: ellipsis;
}
.spu-value i {
  margin-left: 6px;
  color: #0d4fe6;
  font-size: 14px;
}
.code-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 12px auto 0;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: #f2f6ff;
  color: #0d4fe6;
  font-size: 18px;
  cursor: pointer;
}

.product-info {
  display: grid;
  grid-template-columns: 60px minmax(0, 1fr);
  align-items: start;
  gap: 10px;
  width: 100%;
}
.product-thumb,
.product-info img { display: block; width: 60px; height: 60px; border-radius: 4px; object-fit: cover; background: #d8d8d8; }
.sku-info { min-width: 0; width: 100%; overflow: hidden; }
.two-lines { display: -webkit-box; overflow: hidden; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.col-option .template > p {
  margin: 0 0 6px;
  color: #333;
  line-height: 20px;
  white-space: nowrap;
}
.tag-row,
.material-row { margin-bottom: 4px; }
.tag-row {
  margin-bottom: 6px;
  padding: 4px 6px;
  border-radius: 4px;
  background: #f4f6fa;
}
.tag-row p {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 0 4px;
  line-height: 18px;
}
.tag-row p:last-child { margin-bottom: 0; }
.tag-row p span { flex: 0 0 auto; color: #666; font-size: 12px; }
.tag-row p em {
  min-width: 0;
  overflow: hidden;
  color: #333;
  font-size: 12px;
  font-style: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.operation-cell { display: flex; flex-direction: column; align-items: center; gap: 6px; text-align: center; }
.operation-cell button {
  height: 34px;
  padding: 0 12px;
  border: 0;
  border-radius: 4px;
  background: #b4272d;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}
.operation-status {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}
.operation-status.no-need { background: #666; }
.operation-cell p { margin: 4px 0 0; color: #333; font-size: 14px; }
.updated-at {
  width: 80px;
}
.updated-at span { display: block; }

.expand-button {
  height: 24px;
  padding: 0 6px;
  border: 0;
  border-radius: 4px;
  background: #f6f6f6;
  color: #666;
  font-size: 12px;
  cursor: pointer;
}

.expand-content {
  position: relative;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 170fr 173fr 173fr 412fr 181fr 181fr 181fr 105fr;
  width: 100%;
  height: 154px;
  padding: 20px;
  overflow: visible;
  background: #f8f8f8;
}

.expand-content section { box-sizing: border-box; min-width: 0; padding: 0 20px; border-right: 1px solid #dcdfe6; }
.expand-content section:first-child { padding-left: 0; }
.expand-content section:last-child { border-right: 0; }
.expand-content h4 { margin: 0 0 12px; font-size: 16px; font-weight: 600; }
.detail-notice { margin: 0; color: #333; line-height: 20px; white-space: pre-wrap; word-break: break-word; }
.section-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.section-head button { padding: 0; border: 0; background: transparent; color: #b4272d; font-size: 12px; white-space: nowrap; cursor: pointer; }
.image-list { display: flex; flex-wrap: wrap; gap: 12px; }
.image-list .el-image,
.image-placeholder { display: block; width: 60px; height: 60px; border-radius: 4px; object-fit: cover; background: #d8d8d8; }
.image-list .el-image { cursor: zoom-in; }
.file-list { min-height: 0; overflow: hidden; }
.file-list a { display: block; margin: 4px 0; }
.file-categories { display: flex; gap: 28px; overflow: hidden; }
.file-categories > div { display: flex; min-width: 72px; min-height: 0; flex: 1; flex-direction: column; }
.file-categories strong { display: block; flex: 0 0 auto; margin-bottom: 6px; white-space: nowrap; }
.file-category-items { max-height: 72px; min-height: 0; overflow-y: auto; padding-right: 6px; }
.file-categories a { overflow: hidden; color: #0d4fe6; text-overflow: ellipsis; white-space: nowrap; }
.forbid-wrap { position: relative; width: 100%; height: 26px;  cursor: pointer; }
.forbid-list { display: flex; flex-wrap: nowrap; gap: 6px; width: 100%; height: 26px; overflow: hidden; }
.forbid-list span,
.forbid-all span { flex: 0 0 auto; padding: 4px 6px; border: 1px solid #b4272d; border-radius: 4px; color: #b4272d; font-size: 12px; }
.forbid-all { position: absolute; z-index: 20; top: -8px; left: -8px; box-sizing: border-box; display: flex; flex-wrap: wrap; gap: 6px; width: 210px; padding: 8px; border-radius: 4px; background: #fff; box-shadow: 0 4px 14px rgba(0, 0, 0, .14); }

.loading-mask, .empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: #909399;
}
.loading-mask { position: absolute; z-index: 5; inset: 54px 0 0; background: rgba(255, 255, 255, .8); }
.loading-mask i { margin-right: 6px; font-size: 20px; }
</style>
